import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';

const BULB_SPACING = 34;
const BULB_COLORS = ['#ff4a4a', '#3fd06b', '#ffcf3d', '#4ab3ff'];
const KNOCKED_OUT_FOR = 1_600;

const twinkle = keyframes`
  0%, 60% { opacity: 1; }
  100% { opacity: 0.45; box-shadow: 0 0 3px 0 var(--bulb-color); }
`;

// fixed per bulb index so the twinkle pattern doesn't reshuffle on every re-render
const bulbTiming = (index: number) => ({
  duration: 0.9 + ((index * 37) % 15) / 10,
  delay: ((index * 53) % 20) / 10,
});

/**
 * Christmas: a strand of twinkling bulbs hanging from the bottom edge of the header.
 * Click a bulb to knock it out for a moment. Must be rendered inside a positioned header container.
 * `top` is where the wire hangs; the default clears the large home-page logo, which overhangs the header.
 */
export const StringLights = ({ top = 'calc(100% - 2px)' }: { top?: string }) => {
  const [bulbCount, setBulbCount] = useState(() => Math.ceil(window.innerWidth / BULB_SPACING));
  const [knockedOut, setKnockedOut] = useState<Set<number>>(new Set());

  useEffect(() => {
    const onResize = () => setBulbCount(Math.ceil(window.innerWidth / BULB_SPACING));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const knockOut = (index: number) => {
    setKnockedOut((current) => new Set(current).add(index));
    setTimeout(() => {
      setKnockedOut((current) => {
        const next = new Set(current);
        next.delete(index);
        return next;
      });
    }, KNOCKED_OUT_FOR);
  };

  return (
    <Box sx={[sxStyles.strand, { top }]} aria-hidden>
      {Array.from({ length: bulbCount }, (_, i) => {
        const { duration, delay } = bulbTiming(i);
        const isOut = knockedOut.has(i);
        return (
          <Box
            key={i}
            component='button'
            type='button'
            tabIndex={-1}
            onClick={() => knockOut(i)}
            style={{ '--bulb-color': BULB_COLORS[i % BULB_COLORS.length] } as CSSProperties}
            sx={[
              sxStyles.bulb,
              // a slight wave so the strand looks hung rather than ruled
              { top: 4 + Math.sin((i / bulbCount) * Math.PI * 6) * 2 },
              isOut
                ? { background: '#555', boxShadow: 'none', animation: 'none' }
                : { animation: `${twinkle} ${duration}s ease-in-out ${delay}s infinite alternate` },
            ]}
          />
        );
      })}
    </Box>
  );
};

const sxStyles = {
  strand: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 18,
    display: 'flex',
    justifyContent: 'space-around',
    borderTop: '1.5px solid #233227',
    pointerEvents: 'none',
    // above the quote page's sticky tab bar (1000)
    zIndex: 1001,
  },
  bulb: {
    position: 'relative',
    width: 7,
    height: 11,
    padding: 0,
    border: 0,
    borderRadius: '45% 45% 50% 50% / 35% 35% 65% 65%',
    background: 'var(--bulb-color)',
    boxShadow: '0 0 8px 2px var(--bulb-color)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: 1.5,
      width: 4,
      height: 4,
      borderRadius: '1px',
      background: '#3a463c',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
};
